import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { jobsApi } from '../api/client'
import NewListForm from '../components/lists/NewListForm'
import ListsPanel from '../components/lists/ListsPanel'
import TcgHelpPanel from '../components/lists/TcgHelpPanel'
import CustomCardsPanel from '../components/lists/CustomCardsPanel'
import LayoutOptions from '../components/layout/LayoutOptions'
import { useLayoutStore } from '../stores/layoutStore'
import type { DeckList } from '../stores/listsStore'
import type { Card, Tcg } from '../types'

/** Human-readable label per TCG (for mixed-order hints). */
const TCG_LABEL: Record<Tcg, string> = {
  mtg: 'Magic',
  yugioh: 'Yu-Gi-Oh!',
  onepiece: 'One Piece',
  lorcana: 'Lorcana',
  riftbound: 'Riftbound',
}

/**
 * Deck Builder — "Listas independientes" model.
 *
 * The user pastes decklists; each becomes an independent, named "Lista"
 * (a deck) persisted to localStorage. Lists are TEXT-ONLY (no card images
 * are ever loaded in the frontend — Cristian's decision). Each list can be
 * viewed, edited, and turned into a PDF, alone or combined with others.
 *
 * Lists carry their own TCG. When an order mixes games, the backend groups
 * the cards per game and returns a ZIP with one PDF per TCG.
 */
export default function Builder() {
  const navigate = useNavigate()
  const layoutOptions = useLayoutStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  /**
   * Build the backend JobOptions. `tcg` is only sent for single-game orders;
   * for mixed orders the backend routes by each card's `game` field.
   * 
   * NOTE: We intentionally DON'T send block_size - let the backend calculate
   * the optimal size automatically for maximum efficiency.
   */
  const buildOptions = (tcg?: Tcg) => ({
    page_size: layoutOptions.pageSize,
    cards_per_page: layoutOptions.cardsPerPage,
    bleed_mm: layoutOptions.includeBleed ? 3.0 : 0,
    fronts_only: layoutOptions.frontsOnly,
    include_cut_lines: layoutOptions.includeCutLines,
    cut_line_color: layoutOptions.cutLineColor,
    cut_line_style: layoutOptions.cutLineStyle,
    cut_line_width: layoutOptions.cutLineWidth,
    cut_line_over_cards: layoutOptions.cutLineOverCards,
    ...(layoutOptions.maxPdfMb != null ? { max_pdf_mb: layoutOptions.maxPdfMb } : {}),
    ...(tcg ? { tcg } : {}),
    // DO NOT send block_size - let backend auto-calculate for maximum efficiency
  })

  /**
   * Flatten lists into a card array, tagging each card with its list's game
   * (cards from URL imports already carry `game`; text lists were tagged on
   * creation — this is a safety net for legacy lists).
   */
  const flatten = (lists: DeckList[], includeSideboard: boolean): Card[] => {
    const cards: Card[] = []
    for (const list of lists) {
      const tag = (c: Card): Card => (c.game ? c : { ...c, game: list.tcg })
      cards.push(...list.mainboard.map(tag))
      if (includeSideboard) cards.push(...list.sideboard.map(tag))
    }
    return cards
  }

  /**
   * Check if the total card count is a multiple of 9 (or the configured cards per page).
   * If not, show a confirmation dialog warning about gaps.
   * Returns true if the user confirms to proceed, false otherwise.
   */
  const checkMultipleOfNine = (lists: DeckList[], includeSideboard: boolean): boolean => {
    const cards = flatten(lists, includeSideboard)
    const totalCards = cards.reduce((acc, c) => acc + c.quantity, 0)
    const cardsPerPage = layoutOptions.cardsPerPage

    if (totalCards % cardsPerPage !== 0) {
      const remainder = totalCards % cardsPerPage
      const gaps = cardsPerPage - remainder
      return window.confirm(
        `⚠️ El número total de cartas (${totalCards}) no es múltiplo de ${cardsPerPage}.\n\n` +
        `Te quedarán ${gaps} hueco(s) vacío(s) en la última página.\n\n` +
        `¿Quieres continuar de todas formas?\n\n` +
        `💡 Consejo: Puedes usar el panel "Cartas personalizadas" para añadir imágenes ` +
        `y rellenar los huecos.`
      )
    }
    return true
  }

  /** Create a job from a set of cards and navigate to its progress page. */
  const createJob = async (lists: DeckList[], includeSideboard: boolean) => {
    // Check for multiple of 9 first
    if (!checkMultipleOfNine(lists, includeSideboard)) {
      return // User cancelled
    }

    const cards = flatten(lists, includeSideboard)
    if (cards.length === 0) {
      setError('No hay cartas para generar el PDF')
      return
    }

    // Separate cards with local images from those to resolve.
    const localCards = cards.filter((c) => c._localFile)

    const games = new Set(cards.map((c) => c.game ?? 'mtg'))
    const tcg = games.size === 1 ? ([...games][0] as Tcg) : undefined
    setLoading(true)
    setError(null)
    try {
      let job
      if (localCards.length > 0) {
        // Mixed job: upload local images + resolve remote cards.
        const images = localCards.map((c) => c._localFile!)
        // Rewrite image_url to "local:N" references for the backend.
        const cardsWithRefs = cards.map((c) => {
          if (c._localFile) {
            const idx = localCards.indexOf(c)
            return { ...c, image_url: `local:${idx}` }
          }
          return c
        })
        job = await jobsApi.createMixed(cardsWithRefs, images, buildOptions(tcg))
      } else {
        job = await jobsApi.create(cards, buildOptions(tcg))
      }
      navigate(`/job/${job.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el job')
      setLoading(false)
    }
  }

  /** PDF for a single list (mainboard, optionally + sideboard). */
  const handleGeneratePdf = (list: DeckList, includeSideboard: boolean) => {
    void createJob([list], includeSideboard)
  }

  /** Combined PDF from several lists (one PDF per TCG if they differ). */
  const handleGenerateCombined = (lists: DeckList[], includeSideboard: boolean) => {
    void createJob(lists, includeSideboard)
  }

  return (
    <>
      {/* Help toggle button — small floating button, always visible */}
      <button
        type="button"
        onClick={() => setHelpOpen(!helpOpen)}
        className="fixed left-4 top-24 z-20 w-10 h-10 rounded-full bg-white border border-gray-300 shadow-md flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
        title={helpOpen ? 'Ocultar ayuda' : 'Mostrar ayuda'}
        aria-expanded={helpOpen}
      >
        {helpOpen ? '✕' : '?'}
      </button>

      {/* Collapsible help panel — overlays content when open */}
      {helpOpen && (
        <aside className="fixed left-4 top-36 w-80 z-10 max-h-[calc(100vh-10rem)] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          <TcgHelpPanel />
        </aside>
      )}

      <div className="max-w-6xl mx-auto space-y-8 px-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Deck Builder</h1>
          <p className="text-gray-600 mt-2">
            Pega una decklist para crear una lista. Gestiona tus listas y genera PDFs.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Nueva lista</h2>
              <NewListForm />
            </div>

            <CustomCardsPanel />

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <ListsPanel
                onGeneratePdf={handleGeneratePdf}
                onGenerateCombined={handleGenerateCombined}
              />
            </div>
          </div>

          <div className="space-y-6">
            <LayoutOptions />

            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-lg">Estado</h3>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                  {error}
                </div>
              )}
              {loading && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm">
                  Creando job…
                </div>
              )}
              <p className="text-sm text-gray-500">
                Selecciona una lista (o varias) y pulsa «Generar PDF». El PDF se genera en el
                servidor a partir de los nombres de las cartas.
              </p>
              <p className="text-sm text-gray-500">
                Cada lista guarda su juego ({Object.values(TCG_LABEL).join(', ')}). Si combinas
                listas de juegos distintos, el servidor genera un PDF por juego y los entrega en
                un único ZIP.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}